import {
  FunctionComponent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { SortableHeaderCell } from "react-data-grid";
import { useDispatch, useSelector } from "react-redux";

import { Alert } from "antd";
import classnames from "classnames";
import { DEFAULT_MIDSTREAM_SETTINGS } from "constants/app.constants";
import { MCDANIEL_SEARCH_FOLDER_NAME } from "constants/settings.constants";
import { dequal } from "dequal";
// eslint-disable-next-line no-restricted-imports -- intentional dual-write for cross-tab redux-state-sync; see EVA-6684
import { setSelectedWells as setSelectedWellsRedux } from "store/features/map/mapSlice";
import { RootState } from "store/rootReducer";
import { selectSelectedWells, useMapStore } from "stores/map";
import styled from "styled-components/macro";
import { useDebouncedCallback } from "use-debounce";
import { isNormalizedProductionSummary } from "utils/column/isNormalizedProductionSummary";
import {
  hasOrgGeoColumns,
  heldBackOrgGeoPropertyKeys,
  withVisibleOrgGeoSubgroups
} from "utils/column/withVisibleOrgGeoColumns";
import { getColumnPropertyTree, injectGroupProperty } from "utils/data-table";
import { toLocalYYYYMMDD } from "utils/dateFormatters";

import useFeatureFlags from "hooks/useFeatureFlags";

import { isActiveBinnedGroupMode } from "api/data";

import { IColumnSet, IColumns, IGroupColumn, IPropertyColumnMap } from "models/columns";
import { IGroupByListColumn, IGroupByListItem } from "models/groupBy";
import { INormalizeBySetting } from "models/normalizeBy";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import { useDashboardDispatch } from "components/dashboard/hooks/useDashboardDispatch";
import { GroupByProvider } from "components/groupBy/group-by.context";
import { formatPerformanceDateRange } from "components/groupBy/helpers";
import { useGetCategoryList } from "components/groupBy/hooks/useGetCategoryList";
import { useVisibleOrgGeoJobNames } from "components/org-settings/hooks/useVisibleOrgGeoJobNames";
import { Toast, ToastType, showToast } from "components/shared/Toast";
import { useUserSettings } from "components/user/hooks";

import { useUser } from "../../hooks";
import { EntityKind } from "../../models/entityKind";
import {
  DataTableWidgetKey,
  MidstreamDataTableWidgetKey
} from "../dashboard/constants/widgets.constants";
import "./DataTable.scss";
import { AlertContainer, HeaderContainer, rdgThemeClassname } from "./DataTable.styles";
import DataTableActions, { DataSourceSetting } from "./DataTableActions";
import DataTableBody from "./DataTableBody";
import DataTableColumnChooser from "./DataTableColumnChooser";
import { DraggableHeader } from "./DraggableHeader";
import MonthlyProductionGrid from "./MonthlyProductionGrid";
import MonthlyToolbarLead, {
  TOOLBAR_GAP,
  toolbarLeadingWidth
} from "./MonthlyToolbarLead";
import { ColumnCopyButton, useColumnCopy } from "./columnCopy";
import type { SortDirectionT } from "./context";
import {
  changeSortColumn,
  changeSortDirection,
  setDataTableView,
  setMonthlyView,
  useTable
} from "./context";
import {
  useDataQuery,
  useEntitiesOnMap,
  useLargeLoadConfirmation,
  useOverflowIndex,
  useRows
} from "./hooks";
import { useDataTableColumnOrderMutation } from "./hooks/useDataTableColumnOrderMutation";
import { getMonthlyHeaderColor } from "./monthlyColumns";
import type { SortColumn } from "./types";
import { HeaderRendererProps } from "./types";

/**
 * EVA-882: Pure helper — splice `arr` by moving the element at `fromIdx` to
 * `toIdx` and return the new array. The original array is not mutated.
 *
 * Drag right: element inserts *after* other columns it passes over.
 * Drag left: element inserts *before* other columns it passes over.
 * Both match the POC's intended UX (standard sortable-list semantics).
 *
 * Returns `null` when indices are out-of-range or identical so callers can
 * treat `null` as a no-op.
 */
export function reorderByIndex(
  arr: string[],
  fromIdx: number,
  toIdx: number
): string[] | null {
  if (fromIdx === toIdx) return null;
  if (fromIdx < 0 || fromIdx >= arr.length) return null;
  if (toIdx < 0 || toIdx >= arr.length) return null;
  const next = [...arr];
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  return next;
}

/**
 * EVA-882: Pure helper — given a reorder of `selectedProperties` from `prev`
 * to `next`, map the old positional sort-column key to the new one.
 *
 * EVA's sort uses positional integer keys (`columnKey` = index in
 * `selectedProperties`). After reorder the same property lives at a different
 * index, so the sort must be repaired.
 *
 * Returns:
 *   - A new string index when the sorted property is found in `next`.
 *   - `null` when the sorted property no longer exists (clear the sort).
 *   - `undefined` when `prevColumnKey` is not a valid numeric index (leave
 *     sort unchanged — column key may refer to a non-positional column such as
 *     a frozen column that does not participate in reorder).
 */
export function remapSortColumnKey(
  prev: string[],
  next: string[],
  prevColumnKey: string
): string | null | undefined {
  const prevIdx = Number(prevColumnKey);
  if (isNaN(prevIdx) || prevIdx < 0 || prevIdx >= prev.length) return undefined;
  const sortedProperty = prev[prevIdx];
  const newIdx = next.indexOf(sortedProperty);
  return newIdx !== -1 ? String(newIdx) : null;
}

export function applyNormalizationSuffix(
  column: IGroupByListColumn,
  useNormalizeBy: boolean,
  normalizeBySetting: INormalizeBySetting,
  unitType: number
): IGroupByListColumn {
  if (!useNormalizeBy) return column;
  if (column.group !== "Performance" && !isNormalizedProductionSummary(column))
    return column;
  if (!normalizeBySetting.field) return column;

  const displayName = normalizeBySetting.display || normalizeBySetting.field;
  if (!displayName) return column;

  if (normalizeBySetting.useMultilinearNormalization) {
    return { ...column, title: `${column.title} (normalized)` };
  }

  const per = normalizeBySetting.per;
  if (!per || per <= 0) return column;

  const unit =
    normalizeBySetting.units?.[unitType.toString()] ?? normalizeBySetting.unit ?? "";
  const suffix = unit ? ` per ${per} ${unit}` : ` per ${per}`;
  return { ...column, title: column.title.replace(/\(([^)]+)\)$/, `($1${suffix})`) };
}

/**
 * Returns a copy of propertyMap with "Date Range" subgroup column titles enriched
 * with the selected date range suffix. Returns propertyMap unchanged when either date is null.
 * Exported for testing.
 */
export function buildEnrichedPropertyMap(
  propertyMap: IPropertyColumnMap,
  propertyTree: IGroupColumn[],
  startDate: Maybe<Date>,
  endDate: Maybe<Date>
): IPropertyColumnMap {
  if (startDate == null || endDate == null) return propertyMap;
  const dateSuffix = ` ${formatPerformanceDateRange(
    toLocalYYYYMMDD(startDate),
    toLocalYYYYMMDD(endDate)
  )}`;
  const dateRangeProps = new Set<string>();
  const performanceGroup = propertyTree.find((g) => g.key === "Performance");
  if (performanceGroup) {
    for (const pg of performanceGroup.productGroups) {
      const sg = pg.subgroups.find((s) => s.key === "Date Range");
      if (sg) sg.columns.forEach((col) => dateRangeProps.add(col.property));
    }
  }
  if (dateRangeProps.size === 0) return propertyMap;
  const patched = { ...propertyMap };
  for (const prop of dateRangeProps) {
    if (patched[prop]) {
      patched[prop] = { ...patched[prop], title: `${patched[prop].title}${dateSuffix}` };
    }
  }
  return patched;
}

/**
 * Returns a filtered/enriched property tree for the column chooser.
 * - No date range: removes "Date Range" subgroups from the Performance group.
 * - Date range set: enriches "Date Range" column titles with the formatted date range.
 * Exported for testing.
 */
export function buildFilteredPropertyTree(
  propertyTree: IGroupColumn[],
  startDate: Maybe<Date>,
  endDate: Maybe<Date>
): IGroupColumn[] {
  if (startDate == null || endDate == null) {
    return propertyTree.map((group) => {
      if (group.key !== "Performance") return group;
      return {
        ...group,
        productGroups: group.productGroups.map((pg) => ({
          ...pg,
          subgroups: pg.subgroups.filter((sg) => sg.key !== "Date Range")
        }))
      };
    });
  }
  const dateSuffix = ` ${formatPerformanceDateRange(
    toLocalYYYYMMDD(startDate),
    toLocalYYYYMMDD(endDate)
  )}`;
  return propertyTree.map((group) => {
    if (group.key !== "Performance") return group;
    return {
      ...group,
      productGroups: group.productGroups.map((pg) => ({
        ...pg,
        subgroups: pg.subgroups.map((sg) => {
          if (sg.key !== "Date Range") return sg;
          return {
            ...sg,
            columns: sg.columns.map((col) => ({
              ...col,
              title: `${col.title}${dateSuffix}`
            }))
          };
        })
      }))
    };
  });
}

/**
 * Computes which Date Range property keys to auto-add when a date range becomes active.
 * Adds keys for every Performance product group that already has at least one property selected.
 * Exported for testing.
 */
export function computeDateRangePropsToAdd(
  propertyTree: IGroupColumn[],
  selectedProperties: string[]
): string[] {
  const performanceGroup = propertyTree.find((g) => g.key === "Performance");
  if (!performanceGroup || !selectedProperties.length) return [];
  const dateRangePropsByProduct = new Map<string, string[]>();
  for (const pg of performanceGroup.productGroups) {
    const sg = pg.subgroups.find((s) => s.key === "Date Range");
    if (sg)
      dateRangePropsByProduct.set(
        pg.productKey,
        sg.columns.map((c) => c.property)
      );
  }
  const propsToAdd: string[] = [];
  for (const pg of performanceGroup.productGroups) {
    const allProps = pg.subgroups.flatMap((sg) => sg.columns.map((c) => c.property));
    if (allProps.some((p) => selectedProperties.includes(p))) {
      for (const prop of dateRangePropsByProduct.get(pg.productKey) ?? []) {
        if (!selectedProperties.includes(prop)) propsToAdd.push(prop);
      }
    }
  }
  return propsToAdd;
}

/**
 * Returns selectedProperties with all Date Range property keys removed, or null if
 * nothing was removed (no-op). Exported for testing.
 */
export function computeDateRangeCleanup(
  propertyTree: IGroupColumn[],
  selectedProperties: string[]
): string[] | null {
  const performanceGroup = propertyTree.find((g) => g.key === "Performance");
  if (!performanceGroup) return null;
  const dateRangeProps = new Set<string>();
  for (const pg of performanceGroup.productGroups) {
    const sg = pg.subgroups.find((s) => s.key === "Date Range");
    if (sg) sg.columns.forEach((col) => dateRangeProps.add(col.property));
  }
  if (dateRangeProps.size === 0) return null;
  const filtered = selectedProperties.filter((p) => !dateRangeProps.has(p));
  return filtered.length !== selectedProperties.length ? filtered : null;
}

type DataTableT = {
  id: string;
  initialState: { selectedProperties: string[]; pdenSource: PdenSourceEnum };
};

export function canShowCopyButton(params: {
  propertyKey: Maybe<string>;
  isFacility: boolean;
  hasMidstreamExport: boolean;
}): boolean {
  const isMidstreamColumn = params.propertyKey?.startsWith("Midstream.") ?? false;
  return params.hasMidstreamExport || (!params.isFacility && !isMidstreamColumn);
}
const DataTable: FunctionComponent<DataTableT> = ({ id, initialState }) => {
  const matchedUWIs = [];
  const { hasFeature: hasFeatureFlag } = useFeatureFlags();

  // context and hooks
  const dispatch = useDispatch();
  const setSelectedWells = useMapStore((s) => s.setSelectedWells);
  const setSelectedFacilities = useMapStore((s) => s.setSelectedFacilities);
  const dashboardDispatch = useDashboardDispatch();
  const { hasTrialAccount, isReadonly } = useUser();

  useEntitiesOnMap(); // sync uwi/color hash with entities on Map
  const rows = useRows(); // parse rows from context state
  const [{ toolbarHeight, uwis, entityKind, dataTableView, monthlyView }, tableDispatch] =
    useTable();

  const sessionKey = `${
    entityKind === EntityKind.Facility ? MidstreamDataTableWidgetKey : DataTableWidgetKey
  }::${id}`;
  const toolbarRef = useRef<HTMLDivElement>(null);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  // state from store
  const categories = useSelector((state: RootState) =>
    entityKind == EntityKind.Well
      ? state.groupBy.categoryList
      : state.groupBy.facilityFields
  );
  const selectedWellsOnMap = useMapStore(selectSelectedWells);
  const groupBy = useSelector((state: RootState) =>
    entityKind == EntityKind.Well
      ? state.groupBy.globalGroupBy
      : state.groupBy.globalFacilityFocus
  );

  const isGroupMode = !!groupBy?.mode;
  useOverflowIndex(toolbarRef, dropdownContainerRef, toolbarLeadingWidth(entityKind));

  // REQ-UI-GRP-003 / Issues #9 + #10: detect when the group property is a
  // binned type whose raw labels would be meaningless in the Well view.
  // isActiveBinnedGroupMode encapsulates: bin non-null, BinSize configured,
  // canBin=true, dataType in {Number, Integer, Date}.
  const isBinnedNumeric = useMemo(
    () =>
      isGroupMode && dataTableView === "well" && isActiveBinnedGroupMode(groupBy?.mode),
    [isGroupMode, dataTableView, groupBy?.mode]
  );

  const pdenDataSourceSetting = useSelector(
    (state: RootState) => state.app.pdenDataSourceSetting
  );

  // state
  const [columns, setColumns] = useState([]);
  const [dataSources, setDataSources] = useState<DataSourceSetting>({
    forecastSource: MCDANIEL_SEARCH_FOLDER_NAME,
    pdenSource: PdenSourceEnum.Public,
    rescat: "P+PDP"
  });
  const [lastSettings, setLastSettings] = useState<string>();
  const [propertyTree, setPropertyTree] = useState<IGroupColumn[]>([]);
  const [propertyMap, setPropertyMap] = useState<IPropertyColumnMap>({});
  const [selectedProperties, setSelectedProperties] = useState<string[]>();
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const [sortColumns, setSortColumns] = useState<readonly Readonly<SortColumn>[]>([]);
  const { largeLoadConfirmed, confirmLargeLoad } =
    useLargeLoadConfirmation(selectedProperties);
  const prevIsFetchingRef = useRef(false);

  const {
    copied: showCopySuccessMessage,
    copyColumnContents,
    copyCellValue
  } = useColumnCopy();

  // Track whether a date range was previously active (for null→set transition detection).
  const prevHadDateRangeRef = useRef(false);
  // Mirror selectedProperties in a ref so the auto-select/remove effect can read the
  // latest value without including it in the dependency array (avoids re-running on
  // every column toggle when no date range is active).
  const selectedPropertiesRef = useRef(selectedProperties);
  selectedPropertiesRef.current = selectedProperties;

  // EVA-882: Mirror sortColumns in a ref so handleReorder can compute the new
  // sort index without a stale closure.
  const sortColumnsRef = useRef(sortColumns);
  sortColumnsRef.current = sortColumns;

  // EVA-882: Mirror persistColumnOrder in a ref so handleReorder's empty-dep
  // useCallback can call the debounced function without a stale closure.
  // (persistColumnOrder is declared after handleReorder; putting it in the dep
  // array would create a TDZ ReferenceError — this is the file's established
  // "reads everything via refs" pattern, see L462.)
  const persistColumnOrderRef = useRef<(properties: string[]) => void>(null);

  /**
   * EVA-882: Handle a column header drag-and-drop reorder.
   *
   * fromKey / toKey are String(column.key) — numeric column indices as strings.
   * We splice `selectedProperties` (the source of truth for order + selection),
   * then repair the active sort column index so `onSortColumnsChange` keeps
   * returning the correct property key after the reorder.
   */
  const handleReorder = useCallback(
    (fromKeyStr: string, toKeyStr: string) => {
      const prev = selectedPropertiesRef.current;
      if (!prev) return;

      // EVA-882 FIX 1: once the user interacts, local state is authoritative.
      // Mark the seed guard immediately so a concurrent onSuccess echo (the
      // server echoing back the saved order after the first debounced POST)
      // can never re-run the seed effect and revert a second reorder made
      // while the first save was in flight.
      userDefaultSeedAppliedRef.current = true;

      const fromIdx = Number(fromKeyStr);
      const toIdx = Number(toKeyStr);

      const next = reorderByIndex(prev, fromIdx, toIdx);
      if (!next) return; // no-op: identical indices or out-of-range

      setSelectedProperties(next);
      persistColumnOrderRef.current?.(next); // EVA-882: persist drag-reorder immediately

      // Repair the sort column index: find where the previously-sorted column
      // landed in the new order so the sort arrow stays on the correct column.
      const prevSortCols = sortColumnsRef.current;
      if (prevSortCols.length > 0) {
        const { columnKey, direction } = prevSortCols[0];
        const newKey = remapSortColumnKey(prev, next, columnKey);
        if (newKey === undefined) return; // non-positional key — leave sort unchanged
        if (newKey === null) {
          setSortColumns([]);
        } else {
          setSortColumns([{ columnKey: newKey, direction }]);
        }
      }
    },
    [] // intentionally empty — reads everything via refs
  );

  // REQ-UI-TOG-004: Reset to group view when group mode is turned off
  useEffect(() => {
    if (!isGroupMode) {
      setDataTableView(tableDispatch, "group");
    }
  }, [isGroupMode, tableDispatch]);

  // Get normalization state from store early so it can be used in effectiveProperties
  const useNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.useNormalizeBy
  );
  const globalNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.globalNormalizeBy
  );

  // REQ-UI-GRP-001: Inject outer group property in Well view so it's always
  // fetched regardless of column chooser state.
  // Also inject the denominator field for normalization so we can detect missing values.
  const effectiveProperties = useMemo<string[] | undefined>(() => {
    if (!selectedProperties?.length) return selectedProperties;
    let props = injectGroupProperty(selectedProperties);

    // Inject denominator field if normalization is active
    if (
      useNormalizeBy &&
      globalNormalizeBy?.field &&
      !props.includes(globalNormalizeBy.field)
    ) {
      props = [...props, globalNormalizeBy.field];
    }

    return props;
  }, [
    isGroupMode,
    dataTableView,
    groupBy?.mode?.property,
    selectedProperties,
    useNormalizeBy,
    globalNormalizeBy
  ]);

  // fetches data from API
  const {
    isFetching,
    isSuccess,
    errorMessage,
    droppedNormalizationCount,
    normalizedFieldName,
    pagination,
    pageSize,
    maxPageSize,
    totalResults
  } = useDataQuery(
    effectiveProperties,
    dataSources,
    id,
    largeLoadConfirmed,
    !monthlyView
  );
  const awaitingLargeLoadConfirm = pagination && !largeLoadConfirmed;
  const showPagination = !monthlyView && pagination;

  const activeEntities = useSelector((state: RootState) => state.app.activeEntityKinds);
  const userSettings = useUserSettings();
  const isMidstreamDataSourceEnabled =
    userSettings?.midstreamSettings?.enabled ?? DEFAULT_MIDSTREAM_SETTINGS;
  const isWell = entityKind === EntityKind.Well;
  const isFacility = entityKind === EntityKind.Facility;
  const hasMidstreamExport = hasFeatureFlag("Midstream Export");

  // EVA-882: column-order entityKind discriminator ("Well" | "Facility")
  const columnOrderId = isWell ? "Well" : "Facility";
  const columnOrderMutation = useDataTableColumnOrderMutation(columnOrderId);

  // Guard: has the initial column seed from user-default already been applied
  // once? Prevents re-seeding on every context refetch (which happens after each
  // successful mutation, via query invalidation).
  const userDefaultSeedAppliedRef = useRef(false);

  const hiddenMessage = useMemo(() => {
    if (isWell && !activeEntities.includes(EntityKind.Well)) {
      return "Wells are hidden";
    }
    if (isFacility && !activeEntities.includes(EntityKind.Facility)) {
      return isMidstreamDataSourceEnabled
        ? "Midstream is hidden"
        : "Midstream is disabled";
    }
    return null;
  }, [activeEntities, isMidstreamDataSourceEnabled]);

  // sync selected properties from initial state (snapshot or saved dashboard).
  // When a snapshot governs, mark the hydration ref so the persist effect
  // knows the initial load path has fired.
  useEffect(() => {
    if (!initialState?.selectedProperties) return;
    // Selections saved before EVA-533 can hold duplicate columns.
    setSelectedProperties([...new Set(initialState.selectedProperties)]);
    userDefaultSeedAppliedRef.current = true; // snapshot governs — mark hydrated
    setDataSources(() => ({
      ...dataSources,
      pdenSource: PdenSourceEnum[pdenDataSourceSetting.source]
    }));
  }, [initialState]);

  // EVA-882: once user settings have loaded, lock the ref to prevent re-seeding
  // after column-order mutations invalidate the query. Do NOT restore selectedProperties
  // from the user default — the table always resets to the Header group on page reload.
  // Snapshot columns are applied by the initialState effect above.
  useEffect(() => {
    if (userDefaultSeedAppliedRef.current) return;
    if (userSettings?.dataTableColumnOrder !== undefined) {
      userDefaultSeedAppliedRef.current = true;
    }
  }, [userSettings?.dataTableColumnOrder]);

  // EVA-882: debounced persist of column order to user-svc setting.
  // Called directly from user-action sites (reorder, chooser, column-set apply)
  // to avoid a useEffect that would also fire on the initial load-from-server
  // assignments (snapshot seed and user-default seed).
  // Debounced at 800 ms to absorb rapid drag events.
  //
  // NOTE: do NOT pass `flushOnExit` — use-debounce's flushOnExit path references
  // Node's `global` (for a visibilitychange listener), which is undefined in the
  // Vite browser build and crashes the data table. We get the same "don't drop the
  // last reorder" guarantee by flushing manually on unmount via `.flush()` below.
  const persistColumnOrder = useDebouncedCallback((properties: string[]) => {
    columnOrderMutation.mutate(properties);
  }, 800);
  // EVA-882 FIX 4: keep the ref current after each render so handleReorder's
  // empty-dep useCallback always calls the latest debounced instance.
  persistColumnOrderRef.current = persistColumnOrder;
  // Flush any pending debounced write when the table unmounts so a final reorder
  // made just before unmount is still persisted.
  useEffect(() => () => persistColumnOrder.flush(), [persistColumnOrder]);

  const hasDateRange = dataSources.startDate != null && dataSources.endDate != null;
  const enrichedPropertyMap = useMemo<IPropertyColumnMap>(
    () =>
      buildEnrichedPropertyMap(
        propertyMap,
        propertyTree,
        dataSources.startDate,
        dataSources.endDate
      ),
    [propertyMap, propertyTree, dataSources.startDate, dataSources.endDate]
  );

  // sync columns
  useEffect(() => {
    if (!selectedProperties) return;

    const nextColumns = selectedProperties.reduce(
      (list: (IColumns & { propertyKey?: string })[], propertyKey, index) => {
        const property = enrichedPropertyMap[propertyKey];
        if (property) {
          if (
            property.property === "Header.EntityName" ||
            property.property === "Header.FacilityId"
          ) {
            const isGroupView = isGroupMode && dataTableView === "group";
            list.push({
              key: 0,
              name: isGroupView ? `${groupBy?.mode?.title ?? "Group"}` : property.title,
              propertyKey: property.property,
              width: 230,
              resizable: true,
              sortable: true,
              color: property.hexColor,
              dataType: property.dataType,
              sortOrder: property.sortOrder,
              frozen: true,
              formatter: ({ row }) => getEntityNameCell(row)
            });
          } else {
            list.push({
              key: index,
              name: property.title,
              propertyKey: property.property,
              width: 200,
              resizable: true,
              sortable: true,
              color: property.hexColor,
              dataType: property.dataType,
              sortOrder: property.sortOrder,
              formatter: ({ column, row }) => getFormattedCell(column, row)
            });
          }
        }
        return list;
      },
      []
    );

    // REQ-UI-GRP-002 / REQ-DT-006: Inject frozen Group column in Well view.
    // The backend always appends the bin/group label as an extra element at
    // index `selectedProperties.length` (the slot after all user-requested
    // fields). Reading from that index works whether or not the user also has
    // the group property in their selected columns:
    //   - User does NOT have the property: row = [entity, ...fields, label]
    //     → groupLabelIndex = fields.length → row[groupLabelIndex] = label ✓
    //   - User HAS the property:           row = [entity, raw_val, ...fields, label]
    //     → their column already shows the raw value; the frozen Group column
    //       still reads the label from the last slot ✓
    if (isGroupMode && dataTableView === "well" && groupBy?.mode?.property) {
      // The bin label is always the last element in the row array.
      const groupLabelIndex = selectedProperties.length;

      const groupColumnDef = {
        key: "group-membership", // stable string key — not a numeric index
        name: `${groupBy.mode.title ?? "Group"} (Group)`,
        propertyKey: groupBy.mode.property,
        width: 200,
        resizable: true,
        sortable: true,
        frozen: true,
        color: undefined,
        dataType: groupBy.mode.dataType ?? "Text",
        sortOrder: 0,
        formatter: ({ row }) => (
          <CellWrapper isNumber={false}>{row[groupLabelIndex]}</CellWrapper>
        )
      };
      // Insert at visual position 1 (after entity-name at position 0).
      nextColumns.splice(1, 0, groupColumnDef);
    }

    // EVA-6735: UWI column, always shown for wells immediately to the right of the
    // Entity Name column and sharing its Header-group header colour. Shows the full
    // raw UWI (API14 for US wells, unformatted UWID for Canadian) — the same value
    // that is copied, so the display and the cell/column copy match.
    if (isWell) {
      const entityNameColor = nextColumns.find((c) => c.key === 0)?.color;
      const uwiColumnDef = {
        key: "uwi",
        name: "UWI/API",
        propertyKey: "Header.UWI",
        width: 200,
        resizable: true,
        sortable: false,
        frozen: true,
        color: entityNameColor,
        dataType: "Text" as const,
        sortOrder: 0,
        formatter: ({ row }) => <CellWrapper isNumber={false}>{row.uwi}</CellWrapper>
      };
      // Insert right after the frozen Entity Name column (visual position 1).
      nextColumns.splice(1, 0, uwiColumnDef);
    }

    setColumns([...nextColumns]);
  }, [
    enrichedPropertyMap,
    selectedProperties,
    isGroupMode,
    dataTableView,
    groupBy,
    isWell
  ]);

  const getEntityNameCell = (row): ReactElement => {
    const isGroupView = isGroupMode && dataTableView === "group";
    if (isGroupView) {
      // In group view row.uwi is the group title (the response key). row[0] is
      // the raw Header.EntityName value for one representative well — meaningless
      // in this context. Render the group title with no color dot and no map
      // selection handler (groups are not selectable map entities).
      return <NameCellWrapper>{row.uwi}</NameCellWrapper>;
    }
    return (
      <NameCellWrapper
        onClick={() => {
          if (row.uwi) {
            const obj = {};
            obj[row.uwi] = { Uwi: row.uwi };
            if (isWell) {
              setSelectedWells(obj);
              dispatch(setSelectedWellsRedux(obj));
            } else if (isFacility) {
              setSelectedFacilities(obj);
            }
          }
        }}>
        <EntityColorIcon uwiHex={row.color} />
        {row[0]}
      </NameCellWrapper>
    );
  };

  const getFormattedCell = (column, row): ReactElement => {
    return (
      <CellWrapper
        isNumber={column.dataType === "Number" || column.dataType === "Integer"}>
        {row[column.key]}
      </CellWrapper>
    );
  };

  // Update selected wells on map upon selection of rows on data table
  useEffect(() => {
    // retrieve UWI of wells in data table, both formatted and unformatted
    for (let index = 0; index < rows.length; index++) {
      matchedUWIs.push({
        formatted: rows[index][0],
        unformatted: uwis[index]
      });
    }
    const selectedWells = {};
    selectedRows.forEach((row) => {
      let alternate = "";
      for (let index = 0; index < matchedUWIs.length; index++) {
        if (row === matchedUWIs[index]["formatted"]) {
          alternate = matchedUWIs[index]["unformatted"];
        }
      }
      selectedWells[alternate] = { Uwi: alternate };
    });

    setSelectedWells(selectedWells);
    dispatch(setSelectedWellsRedux(selectedWells));
  }, [selectedRows]);

  // Update rows on data table upon select of wells on map
  useEffect(() => {
    // Get uwis
    const mapWells = Object.keys(selectedWellsOnMap);

    // Clear selected rows if no selected wells on map
    selectedRows.clear();

    // retrieve UWI of wells in data table, both formatted and unformatted
    for (let index = 0; index < rows.length; index++) {
      matchedUWIs.push({
        formatted: rows[index][0],
        unformatted: uwis[index]
      });
    }

    // Add selected rows
    mapWells.forEach((well) => {
      let alternate = "";
      for (let index = 0; index < matchedUWIs.length; index++) {
        if (well == matchedUWIs[index]["unformatted"]) {
          alternate = matchedUWIs[index]["formatted"];
        }
      }
      selectedRows.add(alternate);
    });
  }, [selectedWellsOnMap]);

  // Track any changes in categories
  const { getCategoryListWithUnit, isReady, unitType } = useGetCategoryList();
  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (categories.length === 0) {
      getCategoryListWithUnit(entityKind);
    }
  }, [categories, dispatch, isReady]);

  useEffect(() => {
    if (selectedProperties && selectedProperties.length) {
      const settings = {
        selectedProperties: selectedProperties
      };
      const newSettings = JSON.stringify(settings);

      // Update the session storage.
      sessionStorage.setItem(sessionKey, newSettings);

      if (!!lastSettings && !dequal(lastSettings, newSettings)) {
        // The data table settings have changed, so mark the dashboard as modified.
        dashboardDispatch({
          payload: {
            hasModifiedWidgets: true
          }
        });
      }
      setLastSettings(newSettings);
    }
  }, [selectedProperties]);

  // Show toast when normalization drops are detected
  useEffect(() => {
    const justFinishedFetching = prevIsFetchingRef.current && !isFetching;
    prevIsFetchingRef.current = isFetching;

    // Midstream/facility table should never show normalization-drop toasts.
    if (!justFinishedFetching || !isSuccess || isFacility) return;

    if (droppedNormalizationCount > 0 && normalizedFieldName) {
      const message_text =
        droppedNormalizationCount === 1
          ? `1 value was not normalized due to missing ${normalizedFieldName} data`
          : `${droppedNormalizationCount} values were not normalized due to missing ${normalizedFieldName} data`;

      showToast("normalization-drop", message_text, ToastType.Warning);
    }
  }, [isFetching, isSuccess, droppedNormalizationCount, normalizedFieldName, isFacility]);

  const normalizedCategories = useMemo<IGroupByListItem[]>(() => {
    if (!useNormalizeBy || isFacility) return categories;
    return categories.map((category) => ({
      ...category,
      columns: category.columns?.map((col) =>
        applyNormalizationSuffix(col, useNormalizeBy, globalNormalizeBy, unitType)
      )
    }));
  }, [categories, useNormalizeBy, globalNormalizeBy, unitType, isFacility]);

  useEffect(() => {
    // Array to store correct order of categories
    const orderedCategories = normalizedCategories.slice().sort(function (a, b) {
      return a.sortOrder - b.sortOrder;
    });

    const [propertyTree, newPropertyMap] = getColumnPropertyTree(
      [...orderedCategories],
      entityKind,
      hasFeatureFlag("Midstream Allocation Model")
    );

    setPropertyTree(propertyTree);
    setPropertyMap(newPropertyMap);
  }, [normalizedCategories]);

  const visibleJobNames = useVisibleOrgGeoJobNames(
    hasOrgGeoColumns(normalizedCategories)
  );

  // Filter "Date Range" subgroups from column chooser when no date range is set;
  // when a date range IS set, enrich those column titles with the formatted date.
  // EVA-148: only the chooser loses a hidden job's columns. propertyMap keeps
  // them so a column already in the table or a saved layout still gets its header.
  const filteredPropertyTree = useMemo<IGroupColumn[]>(
    () =>
      withVisibleOrgGeoSubgroups(
        buildFilteredPropertyTree(
          propertyTree,
          dataSources.startDate,
          dataSources.endDate
        ),
        visibleJobNames
      ),
    [propertyTree, dataSources.startDate, dataSources.endDate, visibleJobNames]
  );

  const heldBackOrgGeoKeys = useMemo(
    () => heldBackOrgGeoPropertyKeys(propertyTree, visibleJobNames),
    [propertyTree, visibleJobNames]
  );

  // Auto-select/remove Date Range subgroup columns on date range transitions.
  // Reads selectedProperties via ref to avoid re-running on every column toggle.
  useEffect(() => {
    const selectedProps = selectedPropertiesRef.current;
    const prevHadDateRange = prevHadDateRangeRef.current;

    // Transition: null → set — auto-select Date Range columns for selected Performance products
    if (hasDateRange && !prevHadDateRange) {
      const propsToAdd = computeDateRangePropsToAdd(propertyTree, selectedProps ?? []);
      if (propsToAdd.length > 0) {
        setSelectedProperties([...(selectedProps ?? []), ...propsToAdd]);
      }
    }

    // Transition: set → null (or initial mount with stale date range props) —
    // remove Date Range properties whenever no date range is active.
    if (!hasDateRange && selectedProps?.length) {
      const cleaned = computeDateRangeCleanup(propertyTree, selectedProps);
      if (cleaned !== null) setSelectedProperties(cleaned);
    }

    prevHadDateRangeRef.current = hasDateRange;
  }, [hasDateRange, propertyTree]);

  const handleCellClick = (_, row, column) => {
    if (hasTrialAccount || isReadonly) {
      return;
    }
    if (
      !canShowCopyButton({
        propertyKey: column.propertyKey,
        isFacility,
        hasMidstreamExport
      })
    ) {
      return;
    }
    copyCellValue(row[column.key]);
  };

  // Create columns that can be sorted
  const sortableColumns = useMemo(() => {
    type HeaderRendererPropsWithPropertyKey = Omit<
      HeaderRendererProps<never>,
      "column"
    > & {
      column: HeaderRendererProps<never>["column"] & { propertyKey?: string };
    };
    // Custom header renderer function
    function HeaderRenderer(props: HeaderRendererPropsWithPropertyKey) {
      // SortableHeaderCell is the element react-data-grid uses if custom header renderer is not used but columns are sortable
      const colKey = props.column.key;

      // EVA-882: frozen columns (key 0 = entity name, "group-membership") are
      // excluded from the drag range — they must never be dragged or receive drops.
      // Compare as string to avoid number/string type mismatch (IColumns.key is
      // number | string but RDG CalculatedColumn.key is typed as string).
      const isFrozenCol =
        String(colKey) === "0" ||
        colKey === "group-membership" ||
        props.column.frozen === true;

      const handleColumnCopyClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        copyColumnContents(props.column.name, rows, props.column.key);
      };
      const showCopyButton = canShowCopyButton({
        propertyKey: props.column.propertyKey,
        isFacility,
        hasMidstreamExport
      });

      if (!isFrozenCol) {
        // Non-frozen column: render via DraggableHeader which owns the drag
        // event handlers. The copy button lives inside it.
        return (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          <SortableHeaderCell {...props}>
            <DraggableHeader
              columnKey={String(colKey)}
              name={props.column.name?.toString() ?? ""}
              color={props.column.color}
              frozen={false}
              onReorder={handleReorder}>
              {!hasTrialAccount && !isReadonly && showCopyButton && (
                <ColumnCopyButton
                  open={showCopySuccessMessage[colKey] || false}
                  onClick={handleColumnCopyClick}
                />
              )}
            </DraggableHeader>
          </SortableHeaderCell>
        );
      }

      // Frozen column: original rendering, no drag affordances.
      return (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        <SortableHeaderCell {...props}>
          <HeaderContainer
            title={props.column.name?.toString() ?? ""} //tooltip
            style={{
              color: props.column.color,
              backgroundColor: props.column.color + 20,
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
              position: "fixed",
              left: "0px",
              right: "0px",
              textIndent: "5px"
            }}>
            {props.column.name}
            {!hasTrialAccount && !isReadonly && showCopyButton && (
              <ColumnCopyButton
                open={showCopySuccessMessage[colKey] || false}
                onClick={handleColumnCopyClick}
              />
            )}
          </HeaderContainer>
        </SortableHeaderCell>
      );
    }

    // Return columns with the header renderer applied
    return columns.map((c) => {
      if (c.key === "select-row") {
        return c;
      }
      return { ...c, headerRenderer: HeaderRenderer };
    });
  }, [
    columns,
    rows,
    showCopySuccessMessage,
    hasMidstreamExport,
    isFacility,
    handleReorder
  ]);

  const onSortColumnsChange = useCallback(
    (sortColumns: SortColumn[]) => {
      const { columnKey, direction } = sortColumns.length
        ? sortColumns[0]
        : { columnKey: 0, direction: "NONE" };
      const sortDirection = direction.toLocaleLowerCase() as SortDirectionT;

      const sortColumn = selectedProperties[columnKey];
      setSortColumns(sortColumns.slice(-1));
      changeSortColumn(tableDispatch, sortColumn);
      changeSortDirection(tableDispatch, sortDirection);
    },
    [selectedProperties, tableDispatch]
  );

  // Custom sorting function that moves n/a values to the end
  const sortedRows = useMemo(() => {
    if (!sortColumns.length || sortColumns[0].direction === "NONE") {
      return rows;
    }

    const { columnKey, direction } = sortColumns[0];

    const getValue = (row: (typeof rows)[number], key: typeof columnKey) => {
      // In group view the first column (key "0") displays row.uwi (the group label),
      // not row["0"] (a representative well's entity name). Sort on the displayed value.
      if (isGroupMode && dataTableView === "group" && key === "0") return row.uwi;
      return row[key];
    };

    return [...rows].sort((a, b) => {
      const aValue = getValue(a, columnKey);
      const bValue = getValue(b, columnKey);

      // Check for n/a values (null, undefined, empty string, "n/a", "N/A", etc.)
      const aIsNA =
        aValue === null ||
        aValue === undefined ||
        aValue === "" ||
        (typeof aValue === "string" &&
          (aValue.toLowerCase().trim() === "n/a" ||
            aValue.toLowerCase().trim() === "na" ||
            aValue.trim() === "-" ||
            aValue.trim() === ""));
      const bIsNA =
        bValue === null ||
        bValue === undefined ||
        bValue === "" ||
        (typeof bValue === "string" &&
          (bValue.toLowerCase().trim() === "n/a" ||
            bValue.toLowerCase().trim() === "na" ||
            bValue.trim() === "-" ||
            bValue.trim() === ""));

      // Move n/a values to the end regardless of sort direction
      if (aIsNA && !bIsNA) return 1;
      if (!aIsNA && bIsNA) return -1;
      if (aIsNA && bIsNA) return 0;

      // Check if both values are numbers
      const aNum = Number(aValue);
      const bNum = Number(bValue);
      const aIsNumber = !isNaN(aNum) && isFinite(aNum);
      const bIsNumber = !isNaN(bNum) && isFinite(bNum);

      // If both are numbers, sort numerically
      if (aIsNumber && bIsNumber) {
        if (direction === "ASC") {
          return aNum - bNum;
        } else {
          return bNum - aNum;
        }
      }

      // Normal sorting for non-numeric values (strings)
      if (direction === "ASC") {
        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
      } else {
        if (aValue > bValue) return -1;
        if (aValue < bValue) return 1;
        return 0;
      }
    });
  }, [rows, sortColumns, selectedProperties, isGroupMode, dataTableView]);

  const gridClassnames = classnames(rdgThemeClassname);

  // Rows hold values by column position, so the previous load's rows would sit
  // under the wrong headers until the new columns are confirmed and fetched.
  const visibleRows = awaitingLargeLoadConfirm ? [] : sortedRows;

  return (
    <Wrapper
      className={`${showPagination ? "paginationToolbar" : ""}`}
      toolbarHeight={toolbarHeight}>
      {hiddenMessage && (
        <AlertContainer>
          <Alert message={hiddenMessage} type="warning" />
        </AlertContainer>
      )}

      <Toolbar ref={toolbarRef}>
        <MonthlyToolbarLead
          entityKind={entityKind}
          monthly={monthlyView}
          onToggle={() => setMonthlyView(tableDispatch, !monthlyView)}
          chooser={
            <DataTableColumnChooser
              categories={filteredPropertyTree}
              onPropertiesChanged={(propertyKeys) => {
                // EVA-882 FIX 1: chooser interaction — local state is now authoritative.
                userDefaultSeedAppliedRef.current = true;
                setSelectedProperties(propertyKeys);
                persistColumnOrderRef.current?.(propertyKeys); // EVA-882: persist chooser selection
              }}
              selectedPropertyKeys={selectedProperties}
              retainedPropertyKeys={heldBackOrgGeoKeys}
              disabledGroupProperty={
                isBinnedNumeric && groupBy?.mode?.property
                  ? {
                      key: groupBy.mode.property,
                      title: groupBy.mode.title ?? "Group",
                      tooltip:
                        "Group column is unavailable for binned numeric properties. " +
                        "Exact group labels will be supported in a future update."
                    }
                  : undefined
              }
            />
          }
        />
        <GroupByProvider entityKind={entityKind}>
          <DataTableActions
            dropdownContainerRef={dropdownContainerRef}
            selectedProperties={selectedProperties}
            onSelectedColumnSetChanged={(columnSet: IColumnSet) => {
              setSelectedProperties([...new Set(columnSet.properties)]);
            }}
            dataSources={dataSources}
            initialState={initialState}
            onSourcesChanged={(sources) => {
              const settings = {
                selectedProperties: selectedProperties,
                pdenSource: sources.pdenSource
              };
              const newSettings = JSON.stringify(settings);
              sessionStorage.setItem(sessionKey, newSettings);
              setDataSources({
                ...sources
              });
            }}
            rows={visibleRows}
            columns={columns}
          />
        </GroupByProvider>
      </Toolbar>

      {monthlyView ? (
        <MonthlyProductionGrid
          rowsHidden={!!hiddenMessage}
          headerColor={getMonthlyHeaderColor(enrichedPropertyMap)}
        />
      ) : (
        <DataTableBody
          className={gridClassnames}
          columns={sortableColumns}
          rows={!hiddenMessage ? visibleRows : []}
          sortColumns={sortColumns}
          selectedRows={selectedRows}
          toolbarHeight={toolbarHeight}
          pagination={{ show: pagination, pageSize, maxPageSize, totalResults }}
          awaitingLargeLoadConfirm={awaitingLargeLoadConfirm}
          errorMessage={errorMessage}
          isFetching={isFetching}
          onSortColumnsChange={onSortColumnsChange}
          onSelectedRowsChange={setSelectedRows}
          onRowClick={handleCellClick}
          onConfirmLargeLoad={confirmLargeLoad}
        />
      )}

      <Toast toastId="normalization-drop" />
    </Wrapper>
  );
};

export default DataTable;

const Wrapper = styled.div`
  display: grid;
  grid-template-rows: ${(p) => Number(p.toolbarHeight) || "50"}px 1fr;

  &.paginationToolbar {
    grid-template-rows: ${(p) => Number(p.toolbarHeight) || "50"}px 1fr 40px;
  }

  width: 100%;
  height: 100%;
`;

const Toolbar = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr minmax(0, max-content);
  gap: ${TOOLBAR_GAP}px;
  align-items: center;
  padding: 8px;
`;

const CellWrapper = styled.div`
  font-family: var(--fontMono);
  text-align: ${(props) => (props.isNumber ? "right" : "left")};
`;
const NameCellWrapper = styled.div`
  font-family: var(--fontMono);
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;
const EntityColorIcon = styled.span`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  background-color: ${(p) => p.uwiHex};
  border-radius: 2px;
`;
